import { z } from "zod";
export const RegionCodeSchema = z.enum([
    "DAEJEON",
    "SEJONG",
    "CHUNGBUK",
    "CHUNGNAM",
]);
export const ServiceTypeSchema = z.enum([
    "EMERGENCY",
    "NIGHT_CLINIC",
    "PHARMACY",
]);
export const TriageLevelSchema = z.enum([
    "RED_119",
    "ER_NOW",
    "NIGHT",
    "TOMORROW",
    "PHARMACY",
    "ASK_MORE",
]);
export const FreshnessSchema = z.enum([
    "RECENT",
    "DELAYED",
    "EXPIRED",
    "UNAVAILABLE",
]);
export const DataStateSchema = z.enum([
    "CURRENT",
    "PARTIAL",
    "NO_DATA_CONFIRMED",
    "UPSTREAM_ERROR",
    "STALE_ONLY",
    "UNAVAILABLE",
]);
export const RelationSchema = z.enum([
    "SELF",
    "CHILD",
    "ADULT_OTHER",
    "OLDER_ADULT",
]);
export const SymptomCodeSchema = z.enum([
    "FEVER",
    "BREATHING_DIFFICULTY",
    "CHEST_PAIN",
    "ALTERED_MENTAL_STATUS",
    "SEIZURE",
    "SEVERE_BLEEDING",
    "DEHYDRATION",
    "ABDOMINAL_PAIN",
    "TRAUMA",
    "RASH",
    "VOMITING",
    "DIARRHEA",
    "OTHER",
]);
export const RedFlagAnswerSchema = z.enum(["YES", "NO", "UNKNOWN"]);
export const RedFlagAnswersSchema = z.object({
    unresponsive: RedFlagAnswerSchema,
    not_breathing_or_gasping: RedFlagAnswerSchema,
    severe_breathing_difficulty: RedFlagAnswerSchema,
    ongoing_seizure: RedFlagAnswerSchema,
    uncontrolled_bleeding: RedFlagAnswerSchema,
});
export const CoordinatesSchema = z.object({
    kind: z.literal("coordinates"),
    latitude: z.number().min(35.7).max(37.4),
    longitude: z.number().min(125.9).max(128.8),
    accuracyMeters: z.number().positive().max(20_000).optional(),
});
export const AddressLocationSchema = z.object({
    kind: z.literal("address"),
    address: z.string().trim().min(2).max(200),
    region: RegionCodeSchema,
});
export const LocationInputSchema = z.discriminatedUnion("kind", [
    CoordinatesSchema,
    AddressLocationSchema,
]);
const FreeTextSchema = z
    .string()
    .trim()
    .max(500)
    .refine((value) => !/(?:https?:\/\/|www\.)/iu.test(value), {
    message: "URL은 입력할 수 없습니다.",
});
export const BriefingRequestSchema = z.object({
    patient: z.object({
        relation: RelationSchema,
        ageMonths: z.number().int().min(0).max(1_560),
    }),
    symptoms: z.array(SymptomCodeSchema).min(1).max(12),
    onsetAt: z.iso.datetime({ offset: true }),
    redFlags: RedFlagAnswersSchema,
    freeText: FreeTextSchema.optional(),
    location: LocationInputSchema,
    consent: z.object({
        healthProcessing: z.literal(true),
        locationProcessing: z.literal(true),
        policyVersion: z.string().trim().min(1).max(40),
        acceptedAt: z.iso.datetime({ offset: true }),
    }),
});
export const FacilitySearchRequestSchema = z.object({
    location: z.union([CoordinatesSchema, z.object({ kind: z.literal("region"), region: RegionCodeSchema })]),
    serviceType: ServiceTypeSchema,
    radiusKm: z.number().min(1).max(50).default(20),
    locationConsent: z.literal(true),
    name: z.string().trim().max(80).optional(),
    district: z.string().trim().min(1).max(80).optional(),
    offset: z.number().int().min(0).max(100_000).optional(),
    pageVersion: z.string().regex(/^[a-f0-9]{64}$/u).optional(),
    departmentCode: z.string().regex(/^\d{2}$/u).optional(),
    specialistCode: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/u).optional(),
    equipmentCode: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/u).optional(),
    treatmentCode: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/u).optional(),
}).refine((value) => (value.offset ?? 0) === 0 || value.pageVersion !== undefined, {
    message: "이어서 조회하려면 이전 검색 결과 확인값이 필요합니다.",
    path: ["pageVersion"],
}).refine((value) => value.departmentCode === undefined || value.serviceType === "NIGHT_CLINIC", {
    message: "진료과목은 병·의원 조회에서만 선택할 수 있습니다.",
    path: ["departmentCode"],
}).refine((value) => value.serviceType === "NIGHT_CLINIC" || [value.specialistCode, value.equipmentCode, value.treatmentCode].every((code) => code === undefined), {
    message: "전문의·장비·특수진료는 병·의원 조회에서만 선택할 수 있습니다.",
    path: ["serviceType"],
});
export const SourceEvidenceSchema = z.object({
    source: z.enum(["NMC", "HIRA", "NMC_HOLIDAY", "VERIFIED_STATIC"]),
    sourceFacilityId: z.string().min(1),
    sourceUrl: z.url(),
    observedAt: z.iso.datetime({ offset: true }).nullable(),
    fetchedAt: z.iso.datetime({ offset: true }),
});
export const DepartmentSchema = z.object({ code: z.string().regex(/^\d{2}$/u), name: z.string().min(1).max(80) });
export const ReportedHoursSchema = z.object({
    weekly: z.array(z.object({ day: z.enum(["월", "화", "수", "목", "금", "토", "일"]), start: z.string().regex(/^\d{2}:\d{2}$/u), end: z.string().regex(/^\d{2}:\d{2}$/u) })).max(7),
    notes: z.array(z.object({ label: z.string(), text: z.string().min(1).max(500) })).max(5),
    // Additive field keeps snapshots readable by the previous release (notes max 5).
    saturdayLunch: z.string().min(1).max(500).optional(),
});
export const CapabilityItemSchema = z.object({ code: z.string().regex(/^[a-zA-Z0-9_-]{1,40}$/u), name: z.string().min(1).max(120), count: z.number().int().nonnegative().nullable() });
const CapabilityPartSchema = z.object({ fetchedAt: z.iso.datetime({ offset: true }).nullable(), failed: z.boolean(), items: z.array(CapabilityItemSchema).max(100) });
export const HiraCapabilitiesSchema = z.object({ attemptedAt: z.iso.datetime({ offset: true }), specialists: CapabilityPartSchema, equipment: CapabilityPartSchema, treatments: CapabilityPartSchema });
export const HiraDetailsSchema = z.object({
    attemptedAt: z.iso.datetime({ offset: true }),
    departmentError: z.boolean(),
    hoursError: z.boolean(),
    departments: z.object({ fetchedAt: z.iso.datetime({ offset: true }), items: z.array(DepartmentSchema).max(100) }).optional(),
    hours: z.object({ fetchedAt: z.iso.datetime({ offset: true }), data: ReportedHoursSchema }).optional(),
    capabilities: HiraCapabilitiesSchema.optional(),
});
const DetailStatusSchema = z.enum(["CURRENT", "LAST_KNOWN", "NOT_PROVIDED", "NOT_COLLECTED", "EXPIRED", "UNAVAILABLE"]);
const CapabilityViewPartSchema = z.object({ status: DetailStatusSchema, fetchedAt: z.string().nullable(), items: z.array(CapabilityItemSchema) });
export const HiraCapabilitiesViewSchema = z.object({ specialists: CapabilityViewPartSchema, equipment: CapabilityViewPartSchema, treatments: CapabilityViewPartSchema });
export const HiraDetailsViewSchema = z.object({
    sourceUrl: z.url(),
    departments: z.object({ status: DetailStatusSchema, fetchedAt: z.string().nullable(), items: z.array(DepartmentSchema) }),
    hours: z.object({ status: DetailStatusSchema, fetchedAt: z.string().nullable(), data: ReportedHoursSchema.nullable() }),
    capabilities: HiraCapabilitiesViewSchema.optional(),
});
const PharmacyIntervalSchema = z.object({ start: z.string().regex(/^(?:[01]\d|2[0-3]):[0-5]\d$/u), end: z.string().regex(/^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/u), nextDay: z.boolean() });
export const PharmacyHoursDataSchema = z.object({
    weekly: z.array(PharmacyIntervalSchema.extend({ day: z.enum(["월", "화", "수", "목", "금", "토", "일"]) })).max(7),
    holiday: PharmacyIntervalSchema.nullable(),
    invalidDays: z.array(z.number().int().min(1).max(8)).max(8),
});
export const PharmacyIdentitySchema = z.object({ name: z.string().min(1).max(200), address: z.string().min(1).max(400), phone: z.string().min(3).max(40), latitude: z.number().min(-90).max(90), longitude: z.number().min(-180).max(180) });
export const PharmacyHoursDetailsSchema = z.object({
    sourceFacilityId: z.string().min(1).max(40),
    identity: PharmacyIdentitySchema,
    matchMethod: z.literal("EXACT_NAME_ADDRESS_PHONE_COORDINATES_V1"),
    matchedAt: z.iso.datetime({ offset: true }),
    fetchedAt: z.iso.datetime({ offset: true }),
    failed: z.boolean(),
    data: PharmacyHoursDataSchema,
});
export const PharmacyHoursViewSchema = z.object({
    status: z.enum(["CURRENT", "LAST_KNOWN", "NOT_VERIFIED", "NOT_PROVIDED", "EXPIRED"]),
    sourceUrl: z.url(),
    fetchedAt: z.iso.datetime({ offset: true }).nullable(),
    data: PharmacyHoursDataSchema.nullable(),
});
export const FacilitySchema = z.object({
    id: z.string().min(1),
    mappingStatus: z.enum(["VERIFIED", "UNVERIFIED", "AMBIGUOUS"]),
    name: z.string().min(1),
    region: RegionCodeSchema,
    address: z.string().min(1),
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    phone: z.string().min(3).nullable(),
    emergencyPhone: z.string().min(3).nullable(),
    services: z.array(ServiceTypeSchema),
    capabilities: z.array(z.string()),
    availableEmergencyBeds: z.number().int().min(0).nullable(),
    operatingNow: z.boolean().nullable(),
    notice: z.string().max(2_000).nullable(),
    evidence: z.array(SourceEvidenceSchema).min(1),
    hiraDetails: HiraDetailsSchema.optional(),
    pharmacyHoursDetails: PharmacyHoursDetailsSchema.optional(),
});
export const SourceHealthSchema = z.object({
    source: z.string().min(1),
    region: RegionCodeSchema,
    status: z.enum(["SUCCESS", "PARTIAL", "FAILED"]),
    fetchedAt: z.iso.datetime({ offset: true }),
    validRows: z.number().int().min(0),
    expectedRows: z.number().int().min(0).nullable(),
    errorCode: z.string().max(100).nullable(),
    lastSuccessAt: z.iso.datetime({ offset: true }).nullable().optional(),
});
export const FacilitySnapshotSchema = z.object({
    schemaVersion: z.literal(1),
    generatedAt: z.iso.datetime({ offset: true }),
    regions: z.array(RegionCodeSchema).min(1),
    facilities: z.array(FacilitySchema),
    sourceHealth: z.array(SourceHealthSchema),
    hiraDetailBudget: z.object({ day: z.string(), bucket: z.number().int(), requests: z.number().int().nonnegative() }).optional(),
    pharmacyHoursSync: z.object({ attemptedAt: z.iso.datetime({ offset: true }), reservedRequests: z.literal(40), regions: z.array(z.object({ region: RegionCodeSchema, status: z.enum(["SUCCESS", "FAILED"]), rows: z.number().int().nonnegative(), excludedRows: z.number().int().nonnegative().optional(), matched: z.number().int().nonnegative(), errorCode: z.string().max(60).nullable() })).max(4) }).optional(),
});
// Presentation-only state. Never persists a successful refresh timestamp that
// did not occur, and never treats directory freshness as an ER observation.
export const FacilityInformationSchema = z.object({
    directory: z.object({ fetchedAt: z.string().nullable(), refreshDue: z.boolean() }),
    emergency: z.object({ freshness: FreshnessSchema, fetchedAt: z.string().nullable(), observedAt: z.string().nullable() }).nullable(),
});
export const RankedFacilitySchema = FacilitySchema.extend({
    distanceKm: z.number().nonnegative(),
    freshness: FreshnessSchema,
    information: FacilityInformationSchema.optional(),
});
export const BriefingResponseSchema = z.object({
    requestId: z.uuid(),
    triageLevel: TriageLevelSchema,
    action: z.string().min(1),
    reasons: z.array(z.string()).min(1),
    disclaimer: z.string().min(1),
    dataState: DataStateSchema,
    snapshotAt: z.iso.datetime({ offset: true }).nullable(),
    facilities: z.array(RankedFacilitySchema).max(5),
    ruleVersion: z.string().min(1),
});
//# sourceMappingURL=schemas.js.map